<template>
  <div
    class="w-cause"
    :class="elClasses"
    :data-id="cause.id"
    data-type="cause"
  >
    <DraggingIndication />
    <EditableLabel
      ref="label"
      v-model="cause.text"
      class="w-cause__text generic-border--focus-shadow"
      :class="{ 'w-cause__text--editing': editing }"
      :placeholder="$t('message.CAUSE')"
      use-textarea
      @blur="editing = false"
      @input="editCause({ text: $event })"
      @startEdit="preventReinit"
      @keyup="$emit('taResize')"
      @textareaAutoSize="$emit('taResize')"
    />
    <div class="v-centerer w-cause__verif-img-wrapper">
      <CauseVerificationCheckbox :cause="cause" />
      <div class="w-cause__img-wrapper" @click.stop="showFileViewer">
        <PictureCarousel :file-ids="cause.files" no-arrows no-text />
      </div>
    </div>
    <div v-if="!isCauseFalse" class="w-cause__sep" />
    <ActionsPerCauseBtn v-if="!isCauseFalse" :cause="cause" :problem-id="cause.problem" />

    <div v-show="selectingCauses" class="w-cause__overlay" @click="showInSheet = !showInSheet">
      <Checkbox v-model="showInSheet" class="w-cause__overlay__checkbox" round />
    </div>

    <!-- Hidden file editor -->
    <FileViewer
      v-if="fileViewerVisible"
      ref="fileViewer"
      v-model="cause.files"
      pathname="causes/"
      class="w-cause__file-viewer"
      editable
      @input="fileViewerInput"
      @close="fileViewerVisible = false"
    />
  </div>
</template>

<script>
/**
 * Cause item for 5 whys. With a picture display
 */

import $ from 'jquery';
// Vuex
import { EDIT_CAUSE } from '@/store/modules/problem/action-types';
// Components
import ActionsPerCauseBtn from '@/components/problem-sheet/actions-per-cause-btn.vue';
import CauseVerificationCheckbox from '@/components/cause-verification-checkbox.vue';
import Checkbox from '@/components/checkbox.vue';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import PictureCarousel from '@/components/picture-carousel.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';

export default {

  name: 'FwCause',

  components: {
    ActionsPerCauseBtn,
    CauseVerificationCheckbox,
    Checkbox,
    DraggingIndication,
    EditableLabel,
    FileViewer,
    PictureCarousel,
  },

  mixins: [mixinSocketHandler],

  props: {
    cause: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    fwRoot: {
      type: Object,
      default: undefined,
    },
    selectingCauses: Boolean,
  },

  data() {
    return {
      editing: false,
      fileViewerVisible: false,
      causeHadNoFiles: false,
    };
  },

  computed: {
    /** Object: Classes applied to this.$el */
    elClasses() {
      return {
        'w-cause--false': this.isCauseFalse,
      };
    },
    /** Object; Cause's problem object */
    stateProblem() {
      if (this.cause.problem) {
        return this.$store.state.problem.problems[`${this.cause.problem}`];
      }
      return {};
    },
    isCauseFalse() {
      return this.cause.isReal === 0 && this.cause.isVerified;
    },
    /** Boolean: Whether this cause should be displayed in the problem sheet */
    showInSheet: {
      get() {
        return this.cause.hideInSheet !== 1;
      },
      set(val) {
        this.editCause({ hideInSheet: val ? 0 : 1 });
      },
    },
  },

  mounted() {
    $(this.$el).draggable({
      distance: 20,
      addClasses: false,
      revert: 'invalid',
      refreshPositions: true,
      helper: (e) => {
        const clone = $(e.currentTarget).clone();
        clone.addClass('ui-draggable-dragging');
        $(this.fwRoot.$refs.mainWrapper).append(clone);
        return clone;
      },
    });
  },

  beforeDestroy() {
    if ($(this.$el).draggable('instance')) {
      $(this.$el).draggable('destroy');
    }
    if (this.$refs.fileViewer && this.$refs.fileViewer.$el) {
      this.$refs.fileViewer.$el.remove();
    }
  },

  methods: {
    selectCause() {
      this.cause.hideInSheet++;
      if (this.cause.hideInSheet > 1) {
        this.cause.hideInSheet = 0;
      }
      this.fwRoot.$emit('causeInput', this.cause, 'hideInSheet');
    },
    /**
     * Tell the five whys comp to prevent initialization called from the window resize
     * because of the virtual keyboard
     */
    preventReinit() {
      this.editing = true;
      this.fwRoot.preventReinit = true;
    },
    /**
     * Handle edit events
     * @param  {Object} params - Params to send through XHR body (id and problemId will be overridden)
     * @param  {Number} causeId - If the cause to edit isn't this.cause, specify the causeId
     */
    editCause(editParams, causeId) {
      causeId = causeId || this.cause.id;
      const params = { id: causeId, problemId: this.cause.problem, ...editParams };
      this.$store.dispatch(`problem/${EDIT_CAUSE}`, params)
        .then(() => {
          this.sendSocketMessage({ socketCompany: 1, id: this.problemId, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch(this.handleError);
    },
    /** Display the file viewer */
    showFileViewer() {
      this.fileViewerVisible = true;
      this.causeHadNoFiles = this.cause.files.length === 0;
      this.$nextTick(() => {
        this.documentBody.appendChild(this.$refs.fileViewer.$el);
        if (this.causeHadNoFiles) {
          this.$refs.fileViewer.addFile();
        }
      });
    },
    /** Save the cause's files */
    fileViewerInput() {
      const params = { files: this.cause.files };
      if (this.causeHadNoFiles) {
        params.isVerified = 1;
        params.isReal = 1;
      }
      this.editCause(params);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.w-cause {
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  border: 1px solid $color-gray-border;
  border-radius: $high-border-radius;
  padding: 1.5rem 0.7rem 0.3rem 0.7rem;
  z-index: 4;

  .dragging-indication {
    position: absolute;
    top: 1px;
    left: 50%;
    transform: translateX(-50%) rotate(90deg) scale(0.8);
  }

  &--false {
    // When the cause is false, there's no button to add action
    padding-bottom: 0.7rem;
  }

  &__button {
    padding-left: 0;
    padding-right: 0;
  }

  &__text {
    width: calc(100% + .5rem);
    margin-left: 5px;
    background: #fff;
    margin-top: -0.3rem;
    margin-left: -0.3rem;
    border-radius: $main-border-radius;
    box-sizing: border-box;

    &:hover {
      background: $color-gray-background-hover;
    }

    textarea {
      min-height: 3em;
      max-height: 15em;
    }

    .textarea.generic-border--thin-border.generic-border--focus-shadow {
      box-shadow: none;
      border: none;
      background: transparent;
      cursor: pointer;
      font-weight: 600;
      padding: 0.3rem 0.5rem;
      box-sizing: border-box;
      min-height: 3em;
      max-height: 15em;
    }
    .textarea, .editable-label-text {
      padding: 0px;
    }

    &--editing {
      textarea.textarea.generic-border--thin-border.generic-border--focus-shadow {
        cursor: initial;
      }
    }
  }

  &__overlay {
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;

    &__checkbox {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
    }
  }

  &__verif-img-wrapper {
    justify-content: space-between;
    margin-top: 0.4rem;
  }

  &__img-wrapper {
    width: 30px;
    cursor: pointer;
  }

  &__sep {
    height: 1px;
    background: $color-gray-border;
    margin: 0.3rem -0.7rem 0.1rem -1.5rem;
    width: calc(100% + 1.4rem);
  }

  .actions-per-cause-btn__button {
    margin: 0 -0.5rem;
  }
}
.w-cause.ui-draggable-dragging {
  opacity: 0.6;
  cursor: grabbing;
}

.w-cause .w-cause-imgs {
  position: relative;
  margin-top: 3px;
  height: 24px;
  overflow: hidden;
}
.w-cause .w-cause-imgs > img { cursor: pointer; max-height: 100%; max-width: calc(100% - 30px); }
</style>
